import { shopifyComponentCopy } from '@testfeed/product-language'
import {
  shopifyStudyRunStartResponseSchema,
  type ShopifyStudyRunStartResponse,
  type StudyAuthoringState,
} from '@testfeed/shopify-contracts'
import { readCreditExhaustedPrompt, readCreditSpendQuote, type CreditSpendQuote } from './credit-spend-prompt'

/** Satisfied by both the fetch `Response` and the typed client's `ClientResponse`. */
type RunResponse = Pick<Response, 'ok' | 'status' | 'json'>
export type StudyRunRequestResult = { response: RunResponse; payload: ShopifyStudyRunStartResponse }
export type SubmissionRequestResult = StudyRunRequestResult & {
  studyId: string
  scope: { shopDomain: string; orgId: string | null; connectionId: string | null }
  scopeToken: string
}
export type DryRunState = 'checking' | 'ready' | 'blocked' | 'error'
export type CreditPrompt = CreditSpendQuote | null

export function createRunRequestId() {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `shopify-run-${Date.now()}`
}
export async function readRunPayload(response: Pick<Response, 'json'>): Promise<ShopifyStudyRunStartResponse> {
  try {
    const parsed = shopifyStudyRunStartResponseSchema.safeParse(await response.json())
    if (parsed.success) return parsed.data
    console.error('Unable to read Shopify study run response', parsed.error)
  } catch (error) {
    console.error('Unable to read Shopify study run response', error)
  }
  // `success` is the contract's one required field; an unreadable body is simply "not successful".
  return { success: false }
}
export function getRunError(payload: ShopifyStudyRunStartResponse, fallback: string) {
  if (payload.code === 'AUDIENCE_SAMPLE_INSUFFICIENT' && typeof payload.error === 'string') return payload.error
  return payload.code === 'USAGE_CREDITS_EXHAUSTED' ? shopifyComponentCopy.planBilling.limitReachedBody : fallback
}
export function formatCreditBalance(spend: CreditSpendQuote, availableAfterNextRun: number) {
  return shopifyComponentCopy.studyRunPage.capacityBalanceBody
    .replace('{remaining}', String(spend.balance.availableCredits))
    .replace('{remainingAfter}', String(availableAfterNextRun))
}
export function formatCreditUse(spend: CreditSpendQuote) {
  return shopifyComponentCopy.studyRunPage.capacityUseBody.replace('{credits}', String(spend.quotedCredits))
}
export function readDryRunOutcome(data: StudyRunRequestResult | undefined) {
  if (!data)
    return {
      state: 'error' as const,
      prompt: null,
      spend: null,
      noPlan: false,
      availableAfterNextRun: null as number | null,
      preflight: null,
      pageError: null,
    }
  const spend = readCreditSpendQuote(data.payload)
  const succeeded = data.response.ok && data.payload.success === true && Boolean(data.payload.preflightHash)
  const noPlan = succeeded && data.payload.creditBalance?.state === 'no_plan'
  const canRun = data.payload.canRun === true && spend !== null
  const prompt = readCreditExhaustedPrompt(data.payload) ?? (succeeded && spend && !canRun ? spend : null)
  return {
    state:
      prompt || data.payload.code === 'AUDIENCE_SAMPLE_INSUFFICIENT'
        ? ('blocked' as const)
        : succeeded && canRun
          ? ('ready' as const)
          : ('error' as const),
    prompt,
    spend,
    noPlan,
    availableAfterNextRun:
      typeof data.payload.availableAfterNextRun === 'number' ? data.payload.availableAfterNextRun : null,
    preflight:
      succeeded && data.payload.preflight
        ? { ...data.payload.preflight, preflightHash: data.payload.preflightHash }
        : null,
    pageError:
      prompt || (succeeded && canRun) || noPlan
        ? null
        : getRunError(data.payload, shopifyComponentCopy.shopifyApiErrors.studyNotReadyToRun025),
  }
}
export function readSubmissionOutcome(data: SubmissionRequestResult | undefined) {
  if (!data) return { prompt: null, spend: null, availableAfterNextRun: null, pageError: null, started: false }
  const prompt = readCreditExhaustedPrompt(data.payload)
  const spend = readCreditSpendQuote(data.payload)
  const started = data.response.ok && data.payload.success === true && Boolean(data.payload.studyRunId)
  return {
    prompt,
    spend: prompt ? spend : null,
    availableAfterNextRun: prompt && spend ? Math.max(spend.balance.availableCredits - spend.quotedCredits, 0) : null,
    pageError: prompt || started ? null : getRunError(data.payload, shopifyComponentCopy.studyRunPage.runErrorBody),
    started,
  }
}

/**
 * The confirm page's status badge. Once the server's status names the run this page started, its words and tone win
 * (for example "Failed"); the browser's own "Test running" covers only the moment before that re-read arrives.
 */
export function getRunStatus({
  dryRunState,
  isReady,
  hasStarted,
  submittedRunId,
  authoringState,
}: {
  dryRunState: DryRunState
  isReady: boolean
  hasStarted: boolean
  submittedRunId: string | null
  authoringState: StudyAuthoringState | undefined
}) {
  const copy = shopifyComponentCopy.studyRunPage
  const started = followedRun({ hasStarted, submittedRunId, authoringState })
  if (started) return { label: started.label, tone: started.tone }
  if (hasStarted) return { label: copy.runningStatus, tone: 'warning' as const }
  if (dryRunState === 'checking') return { label: copy.checkingStatus, tone: 'info' as const }
  if (isReady) return { label: copy.readyStatus, tone: 'success' as const }
  return { label: copy.notReadyStatus, tone: 'warning' as const }
}

type StartedRun = {
  hasStarted: boolean
  submittedRunId: string | null
  authoringState: StudyAuthoringState | undefined
}

/**
 * The run the confirm page follows: after Run test, only once the server's status names the run this page started (an
 * older run's status is never shown); otherwise, as after a reload, the server's latest run of this saved test when its
 * status describes that run (running, complete, or failed in the run stage).
 */
export function followedRun({ hasStarted, submittedRunId, authoringState }: StartedRun) {
  if (hasStarted) return submittedRunId && authoringState?.latestRunId === submittedRunId ? authoringState : null
  const describesRun =
    authoringState?.status === 'running' || authoringState?.status === 'complete' || authoringState?.stage === 'run'
  return authoringState?.latestRunId && describesRun ? authoringState : null
}

/**
 * The confirm page's summary line, following the server like the badge. Once the server's status names the started
 * run, only its own findings are shown (for example why a run failed); a settled run without findings adds no line.
 * The started line covers only the moments before the server has seen the run, and while it is still running. After a
 * reload the page follows the server's latest run the same way.
 */
export function getRunSummaryBody(run: StartedRun): string | null {
  const copy = shopifyComponentCopy.studyRunPage
  const started = followedRun(run)
  if (!run.hasStarted && !started) return `${copy.savedSetupBody} ${copy.dryRunBody}`
  if (!started || started.status === 'running') return copy.studyStartedBody
  return started.findings.length ? started.findings.map((finding) => finding.message).join(' ') : null
}
